import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import { useFreshUserAttributes } from '@mm/state/hooks/use-fresh-user-attributes';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import PaneSection from '../../../../common/components/PaneSection/PaneSection';
import { SettingsPane } from '../../../../common/components/SettingsPane/SettingsPane';
import { useDesktopNotifications } from '../../../../common/hooks/use-desktop-notifications/use-desktop-notifications';
import { LiveChatPersonaPaneSection } from './LiveChatPersonaPaneSection/LiveChatPersonaPaneSection';
import './MyAccountPanel.pcss';
import { NotificationSettings } from './NotificationSettings/NotificationSettings';
import { ProfilePaneSection } from './ProfilePaneSection/ProfilePaneSection';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { landmineNames } from '@mm/constants/landmine-names';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

export const MyAccountPanel = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const { areUserAttributesUptodate } = useFreshUserAttributes();
  const companyId = useSelector(selectActiveCompanyId);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const modalActions = useModalActions();

  const {
    id: userId,
    userAttributes: { desktopNotifications },
  } = useSelector((state: StoreState) => state.session.session);

  const areDesktopConversationsFollupsOn =
    desktopNotifications?.needsFollowUp ?? false;
  const areDesktopWidgetTriggeredOn =
    desktopNotifications?.widgetTriggered ?? false;

  const {
    openDesktopNotificationsModal,
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  } = useDesktopNotifications();

  useEffect(() => {
    if (oneSignalPlayerId === null) return;
    const desktopDevices = desktopNotifications?.desktopDevices || [];
    const thisDeviceSaved = desktopDevices.includes(oneSignalPlayerId);
    if (!thisDeviceSaved) {
      dispatch(
        usersCRUDActions.updateUserAttributes(companyId, userId, {
          desktopNotifications: {
            ...desktopNotifications,
            desktopDevices: [oneSignalPlayerId, ...desktopDevices],
          },
        }),
      );
    }
  }, [
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  ]);

  if (!areUserAttributesUptodate) {
    return null;
  }

  const desktopNotificationModal = () => {
    if (!featureFlags?.desktopNotifications) {
      return modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['MY_ACCOUNT__ENABLE-DESKTOP-NOTIFICATIONS'],
        feature: 'desktopNotifications',
      });
    }

    return openDesktopNotificationsModal();
  };

  return (
    <SettingsPane className="my-account-panel" title="My Account">
      <ProfilePaneSection />
      <LiveChatPersonaPaneSection />
      <PaneSection
        title="Notification Settings"
        helpHref="https://mobilemonkey.com/help/article/ptx8i6csuz-mobile-monkey-push-notification"
        headerRightSection={() => (
          <>
            {(!notificationServiceWorkerAdded ||
              isUnsubscribedFromNotifications) && (
              <div
                className="my-account-panel__pane--notification-link"
                onClick={desktopNotificationModal}
              >
                Notifications are NOT enabled for this browser. Click to Enable.
              </div>
            )}
          </>
        )}
        className="my-account-panel__pane"
      >
        <div className="my-account-panel__pane-content">
          <div>
            {(areDesktopConversationsFollupsOn ||
              areDesktopWidgetTriggeredOn) &&
              browserNotificationPermissionsStatus &&
              browserNotificationPermissionsStatus !== 'granted' && (
                <div className="my-account-panel__pane--notifications-denied">
                  <i className="my-account-panel__pane--notifications-denied-icon fas fa-exclamation-triangle" />{' '}
                  You have blocked web push notification permissions for this
                  browser. Check out{' '}
                  <a
                    href="https://mobilemonkey.com/help/article/xjrvxif08a-how-to-unblock-notifications-permissions-on-your-browser"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    this help doc
                  </a>{' '}
                  for instructions on unblocking notification permissions.
                </div>
              )}
          </div>
          <div className="my-account-panel__pane-content--setttings">
            <div>NOTIFY ME FOR EVERY CUSTOMER MESSAGE</div>
          </div>
          <div className="my-account-panel__pane-content--setttings-details">
            <NotificationSettings
              notificationType="allMessages"
              deviceType="pushNotifications"
              title="Push notifications on mobile"
              iconType="mobile"
              toolTipText="To get notifications you need to opt in on your desktop browser"
            />
            <NotificationSettings
              notificationType="allMessages"
              deviceType="desktopNotifications"
              title="Push notifications on desktop"
              iconType="desktop"
              toolTipText="To get notifications you need to opt in on your desktop browser"
              notificationChangeCallback={openDesktopNotificationsModal}
              notificationPageChangeCallback={openDesktopNotificationsModal}
            />
          </div>
          <div className="my-account-panel__pane-content--setttings">
            <div>NOTIFY ME WHEN A UNICORN LEAD IS DETECTED</div>
          </div>
          <div className="my-account-panel__pane-content--setttings-details">
            <NotificationSettings
              notificationType="unicornLead"
              deviceType="pushNotifications"
              title="Push notifications on mobile"
              iconType="mobile"
              toolTipText="To get notifications you need to opt in on your desktop browser"
            />
            <NotificationSettings
              notificationType="unicornLead"
              deviceType="desktopNotifications"
              title="Push notifications on desktop"
              iconType="desktop"
              toolTipText="To get notifications you need to opt in on your desktop browser"
              notificationChangeCallback={openDesktopNotificationsModal}
              notificationPageChangeCallback={openDesktopNotificationsModal}
            />
            <NotificationSettings
              notificationType="unicornLead"
              deviceType="emailNotifications"
              title="Email notifications"
              iconType="email"
              toolTipText="Receive notifications on your email address"
            />
          </div>
          <div className="my-account-panel__pane-content--setttings">
            <div>NOTIFY ME WHEN A NOTIFICATION WIDGET IS TRIGGERED</div>
          </div>
          <div className="my-account-panel__pane-content--setttings-details">
            <NotificationSettings
              notificationType="widgetTriggered"
              deviceType="pushNotifications"
              title="Push notifications on mobile"
              iconType="mobile"
              toolTipText="To get notifications you need to opt in on your desktop browser"
            />
            <NotificationSettings
              notificationType="widgetTriggered"
              deviceType="desktopNotifications"
              title="Push notifications on desktop"
              iconType="desktop"
              toolTipText="To get notifications you need to opt in on your desktop browser"
              notificationChangeCallback={openDesktopNotificationsModal}
              notificationPageChangeCallback={openDesktopNotificationsModal}
            />
          </div>
          <div className="my-account-panel__pane-content--setttings">
            <div>NOTIFY ME WHEN A CONVERSATION REQUIRES FOLLOW UP</div>
          </div>
          <div className="my-account-panel__pane-content--setttings-details">
            <NotificationSettings
              notificationType="needsFollowUp"
              deviceType="pushNotifications"
              title="Push notifications on mobile"
              iconType="mobile"
              toolTipText="To get notifications you need to opt in on your desktop browser"
            />
            <NotificationSettings
              notificationType="needsFollowUp"
              deviceType="desktopNotifications"
              title="Push notifications on desktop"
              iconType="desktop"
              toolTipText="To get notifications you need to opt in on your desktop browser"
              notificationChangeCallback={openDesktopNotificationsModal}
              notificationPageChangeCallback={openDesktopNotificationsModal}
            />
          </div>
          <div className="my-account-panel__pane-content--setttings">
            <div>NOTIFY ME WHEN A CONVERSATION WAS ASSIGNED TO ME</div>
          </div>
          <div className="my-account-panel__pane-content--setttings-details">
            <NotificationSettings
              notificationType="assignedToMe"
              deviceType="pushNotifications"
              title="Push notifications on mobile"
              iconType="mobile"
              toolTipText="To get notifications you need to opt in on your desktop browser"
            />
            <NotificationSettings
              notificationType="assignedToMe"
              deviceType="desktopNotifications"
              title="Push notifications on desktop"
              iconType="desktop"
              toolTipText="To get notifications you need to opt in on your desktop browser"
              notificationChangeCallback={openDesktopNotificationsModal}
              notificationPageChangeCallback={openDesktopNotificationsModal}
            />
          </div>
        </div>
      </PaneSection>
    </SettingsPane>
  );
};
